import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { animateScroll as scroll } from 'react-scroll';
import { connect } from 'react-redux';
import { Content, TextEditor } from '../../../components';
import CompareTable, {
  SubCategoryTh,
  Row,
  BenefitTd,
  CategoryTh,
  LegendTh,
  BlankTd,
} from './CompareTableRedesign';
import {
  getCompareScrollingMargin,
  getComparePagePlanIds,
} from '../../../modules/selectors';
import { CONTENT_TYPE_FAST } from '../../../components/Content/Content';
import { updateScrollingMargin } from '../../../modules/comparePageScrolling';

const propTypes = {
  compareScrollMargin: PropTypes.number,
  comparePagePlanIds: PropTypes.arrayOf(PropTypes.object).isRequired,
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      carrierId: PropTypes.number,
      cmsContractId: PropTypes.string,
      cmsPbpId: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string,
      planBenefits: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          id: PropTypes.number,
          name: PropTypes.string,
          order: PropTypes.number,
          planId: PropTypes.number,
          subcategory: PropTypes.string,
          value: PropTypes.string,
        }),
      ),
      region: PropTypes.shape({
        carrierId: PropTypes.number,
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      regionId: PropTypes.number,
    }),
  ),
  editAllowed: PropTypes.bool,
  editBenefitId: PropTypes.number,
  editSubCategoryName: PropTypes.shape({
    value: PropTypes.string,
    benefitName: PropTypes.string,
    categoryName: PropTypes.string,
  }),
  editBenefitName: PropTypes.shape({
    value: PropTypes.string,
    categoryName: PropTypes.string,
    benefitName: PropTypes.string,
  }),
  editCategoryName: PropTypes.shape({}),
  onStartEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
};
const defaultProps = {
  plans: null,
  editAllowed: false,
  editBenefitId: null,
  editSubCategoryName: null,
  editBenefitName: null,
  editCategoryName: null,
  compareScrollMargin: 0,
};

class CompareBenefitsRedesign extends Component {
  componentDidUpdate() {
    const { compareScrollMargin } = this.props;
    scroll.scrollTo(compareScrollMargin, { smooth: 'easeOutSine', horizontal: true, duration: 400 });
  }

  render() {
    const {
      plans,
      editAllowed,
      editBenefitId,
      editBenefitName,
      editCategoryName,
      editSubCategoryName,
      onStartEdit,
      onSaveEdit,
      onCancelEdit,
      comparePagePlanIds,
    } = this.props;
    const planBenefits = [];
    // if we have planIds in redux state, use those to display compare page.
    const newPlans = comparePagePlanIds.length > 0
      ? plans.filter(plan => !comparePagePlanIds.includes(plan.id)) : plans;
    const planIds = [];
    newPlans.forEach((plan) => {
      planIds.push(plan.id);
      plan.planBenefits.forEach((benefit) => {
        if (benefit.planBenefitCategory) {
          // when comparing benefits, always get the name from planBenefitCategory if it exists.
          const changedBenefitName = { ...benefit, name: benefit.planBenefitCategory.name };
          planBenefits.push(changedBenefitName);
        } else planBenefits.push(benefit);
      });
    });
    planBenefits.sort((a, b) => {
      if (a.order === b.order) {
        return a.id - b.id;
      }
      return a.order - b.order;
    });
    const legendRowTds = [];
    for (let i = 0; i < planIds.length; i += 1) {
      legendRowTds.push(<BlankTd column={i} key={i} />);
    }
    const uniqueCategories = [...new Set(planBenefits.map(b => b.category))];
    const tableContent = uniqueCategories.map((category) => {
      const benefitsInCategory = planBenefits.filter(
        b => b.category === category,
      );
      const subcategories = [
        ...new Set(benefitsInCategory.map(b => b.subcategory)),
      ];
      const benefitNames = [...new Set(benefitsInCategory.map(b => b.name))];
      const benefitRows = benefitNames.map((benefitName) => {
        const benefitsWithName = benefitsInCategory.filter(
          b => b.name === benefitName,
        );
        const model = subcategories.map((subcategory, index) => {
          const filterNames = benefitsWithName.filter(
            benefit => benefit.subcategory === subcategory,
          );
          const benefitValues = [];
          let flag = false;
          for (let i = 0; i < planIds.length; i += 1) {
            for (let j = 0; j < filterNames.length; j += 1) {
              if (
                planIds[i] === filterNames[j].planId
                && filterNames[j].value !== ''
              ) {
                const editMode = editAllowed && editBenefitId === filterNames[j].id;
                benefitValues.push(
                  <BenefitTd
                    name="editBenefitId"
                    key={filterNames[j].id}
                    editAvailable={editAllowed && !editMode}
                    value={filterNames[j].id}
                    onClick={editMode === true ? null : onStartEdit}
                    column={i}
                  >
                    <Content
                      value={filterNames[j].value}
                      edit={editMode}
                      inherit
                      onSave={value => onSaveEdit(value, filterNames[j].planId)
                      }
                      onCancel={onCancelEdit}
                      type={CONTENT_TYPE_FAST.PLAIN}
                    />
                  </BenefitTd>,
                );
                flag = true;
                break;
              }
            }
            if (!flag) {
              benefitValues.push(
                <BenefitTd key={`na-${planIds[i]}`} column={i}>
                  N/A
                </BenefitTd>,
              );
            }
            flag = false;
          }

          const isEditable = editAllowed
            && editBenefitName
            && editBenefitName.value === benefitName
            && editBenefitName.categoryName === category;

          const renderBenefitName = (
            <CategoryTh
              name="editBenefitName"
              value={benefitName}
              onClick={
                isEditable === true
                  ? null
                  : event => onStartEdit(event, undefined, category)
              }
              editAvailable={editAllowed}
              colSpan={subcategory === '' ? '2' : '1'}
            >
              <Content
                edit={isEditable}
                inherit
                onSave={onSaveEdit}
                onCancel={onCancelEdit}
                type={CONTENT_TYPE_FAST.PLAIN}
                value={benefitName}
              />
            </CategoryTh>
          );
          const renderBlankCategoryTh = <CategoryTh />;
          const subCategoryEditMode = editSubCategoryName
            && editAllowed
            && editSubCategoryName.value === subcategory
            && editSubCategoryName.benefitName === benefitName
            && editSubCategoryName.categoryName === category;
          const renderSubCategory = (
            <Content
              edit={subCategoryEditMode}
              inherit
              onSave={value => onSaveEdit(value, undefined, benefitName)}
              onCancel={onCancelEdit}
              type={CONTENT_TYPE_FAST.PLAIN}
              value={subcategory}
            />
          );
          return (
            <Row key={subcategory}>
              {index === 0 ? renderBenefitName : renderBlankCategoryTh}
              {subcategory === '' ? null : (
                <SubCategoryTh
                  name="editSubCategoryName"
                  value={subcategory}
                  onClick={
                    subCategoryEditMode === true
                      ? null
                      : event => onStartEdit(event, benefitName, category)
                  }
                  editAvailable={editAllowed && !subCategoryEditMode}
                  benefitName={benefitName}
                >
                  {renderSubCategory}
                </SubCategoryTh>
              )}
              {benefitValues}
            </Row>
          );
        });
        return model;
      });
      const categoryEditMode = !!editAllowed && editCategoryName === category;
      let categoryContent;
      if (categoryEditMode) {
        categoryContent = (
          <TextEditor
            defaultValue={category}
            onCancel={onCancelEdit}
            onSave={onSaveEdit}
            autoSize
            rows={1}
          />
        );
      } else {
        categoryContent = category;
      }
      return (
        <tbody key={category}>
          <Row>
            <LegendTh
              name="editCategoryName"
              key="legend"
              value={category}
              editAvailable={editAllowed && !categoryEditMode}
              onClick={categoryEditMode === true ? null : onStartEdit}
              colSpan="2"
            >
              {categoryContent}
            </LegendTh>
            {legendRowTds}
          </Row>
          {benefitRows}
        </tbody>
      );
    });
    return (
      <div>
        <CompareTable>{tableContent}</CompareTable>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    compareScrollMargin: getCompareScrollingMargin(state),
    comparePagePlanIds: getComparePagePlanIds(state),
  };
}
const mapDispatchToProps = dispatch => ({
  SetMargin: margin => dispatch(updateScrollingMargin(margin)),
});
CompareBenefitsRedesign.defaultProps = defaultProps;
CompareBenefitsRedesign.propTypes = propTypes;
export default connect(mapStateToProps, mapDispatchToProps)(CompareBenefitsRedesign);
